import * as THREE from 'three';
import { simpleNoise } from './utils/noise.js';

/**
 * Góry po bokach drogi - wireframe + nieprzezroczysty fill blokujący słońce.
 *
 * - Dwa pasy (lewy/prawy) flankujące centralną drogę (|x| > roadWidth).
 * - Siatka rows×cols z wysokością z noise(x, worldZ) - góry przewijają się wraz z kamerą.
 * - Fill (czarna, nieprzezroczysta powierzchnia) renderowana pierwsza z depthWrite → blokuje słońce.
 * - Wireframe (InstancedMesh cylindrów) z depthTest:false → zawsze na wierzchu filla.
 * - Recycling: siatka skacze co segmentSpacing i próbuje noise w współrzędnych świata → nieskońcony scroll.
 */
export class Mountains {
    constructor(config = {}) {
        this.config = {
            roadWidth: config.roadWidth || 20,
            stripWidth: config.stripWidth || 80,
            segmentSpacing: config.segmentSpacing || 6,
            rows: config.rows || 30,
            cols: config.cols || 12,
            height: config.height || 40,
            noiseScale: config.noiseScale || 0.04,
            lineWidth: config.lineWidth || 0.1,
            glowIntensity: config.glowIntensity || 0.9,
            baseColor: config.baseColor || 0x15F8E5,
            fillColor: config.fillColor ?? 0x000000,
            baseLineY: config.baseLineY ?? -5,
            solid: config.solid !== false,
            enabled: config.enabled !== false
        };

        this.group = new THREE.Group();
        this.baseColor = this.config.baseColor;
        this.lastSnappedZ = null;
        this.sides = []; // { fillMesh, fillPositions, lineMesh, lineCapacity, points }

        this._buildMaterials();
        this._buildSides();

        if (this.config.enabled) {
            this.rebuild(0);
        }
    }

    /**
     * Materiały: wireframe (depthTest=true) + fill (nieprzezroczysty, polygonOffset).
     * Fill pisze głębokość → odcina linie tylnych gór (góry nie prześwitują przez siebie).
     * polygonOffset odsuwa fill w głąb, żeby wireframe na powierzchni nie z-collidował z własnym fill'em.
     */
    _buildMaterials() {
        const glowIntensity = this.config.glowIntensity || 1.0;
        const baseOpacity = 0.8;
        this.bloomBoost = Math.max(glowIntensity * 1.1, 2.5);

        this.material = new THREE.MeshBasicMaterial({
            color: this.baseColor,
            transparent: true,
            opacity: Math.min(baseOpacity * glowIntensity, 1.0),
            depthTest: true,
            depthWrite: false,
            toneMapped: false
        });
        this.material.color.multiplyScalar(this.bloomBoost);

        // Cylinder jednostkowy osi Y, współdzielony przez wszystkie instancje obu pasów.
        this.unitCylinderGeometry = new THREE.CylinderGeometry(1, 1, 1, 6, 1);

        this.fillMaterial = new THREE.MeshBasicMaterial({
            color: this.config.fillColor ?? 0x000000,
            transparent: false,
            side: THREE.DoubleSide,
            depthWrite: true,
            depthTest: true,
            polygonOffset: true,
            polygonOffsetFactor: 1,
            polygonOffsetUnits: 1,
            toneMapped: false
        });
    }

    /**
     * Zbuduj geometrię fill i pustą siatkę instancji dla obu pasów. X/Z są stałe, Y aktualizowane w rebuild.
     */
    _buildSides() {
        const { rows, cols, roadWidth, stripWidth, baseLineY } = this.config;

        for (const side of ['left', 'right']) {
            const sign = side === 'left' ? -1 : 1;
            const xStart = sign * roadWidth;
            const xEnd = sign * (roadWidth + stripWidth);

            // Pozycje fill: rows*cols wierzchołków, X i Z stałe, Y będzie aktualizowane.
            const positions = new Float32Array(rows * cols * 3);
            for (let row = 0; row < rows; row++) {
                for (let col = 0; col < cols; col++) {
                    const i = (row * cols + col) * 3;
                    const t = col / (cols - 1);
                    positions[i] = xStart + (xEnd - xStart) * t;
                    positions[i + 1] = baseLineY; // placeholder, aktualizowane w rebuild
                    positions[i + 2] = 0; // placeholder (Z lokalne = 0, group trzyma pozycję)
                    // Z zostanie wpisane w rebuild bo zależy od snappedZ.
                }
            }

            // Indeksy: dwa trójkąty na kwadrat.
            const indices = [];
            for (let row = 0; row < rows - 1; row++) {
                for (let col = 0; col < cols - 1; col++) {
                    const a = row * cols + col;
                    const b = a + 1;
                    const c = a + cols;
                    const d = c + 1;
                    // Orientacja zależy od znaku strony - zostawiamy; FrontSide + renderOrder zatroszczą się.
                    indices.push(a, c, b);
                    indices.push(b, c, d);
                }
            }

            const geometry = new THREE.BufferGeometry();
            geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
            geometry.setIndex(indices);
            geometry.computeVertexNormals();

            const fillMesh = new THREE.Mesh(geometry, this.fillMaterial);
            fillMesh.renderOrder = -1;
            fillMesh.visible = this.config.solid;
            fillMesh.frustumCulled = false; // grupa się przemieszcza; bounding sphere mogłoby być nieaktualne
            this.group.add(fillMesh);

            // Punkty siatki (referencje do tych samych wartości w positions).
            const points = [];
            for (let row = 0; row < rows; row++) {
                points[row] = [];
                for (let col = 0; col < cols; col++) {
                    points[row][col] = { x: 0, y: baseLineY, z: 0 };
                }
            }

            this.sides.push({ sign, xStart, xEnd, fillMesh, fillPositions: positions, points, lineMesh: null, lineCapacity: 0 });
        }
    }

    /**
     * Wysokość góry w punkcie (x, worldZ). noise + edgeFactor (góra rośnie od krawędzi drogi).
     */
    _heightAt(x, worldZ, edgeFactor) {
        const { noiseScale, height } = this.config;
        const n1 = simpleNoise(x * noiseScale, worldZ * noiseScale);
        const n2 = simpleNoise(x * noiseScale * 2.3 + 10, worldZ * noiseScale * 2.3 + 10);
        const n = n1 * 0.65 + n2 * 0.35;
        return n * height * edgeFactor;
    }

    /**
     * Przebuduj siatkę dla zadanego snappedZ (pozycja Z grupy).
     * Aktualizuje Y filla i macierze instancji wireframe.
     */
    rebuild(snappedZ) {
        if (!this.config.enabled) return;

        const { rows, cols, segmentSpacing, baseLineY, roadWidth, stripWidth } = this.config;
        this.group.position.set(0, 0, snappedZ);

        for (const side of this.sides) {
            const { fillPositions, points } = side;
            const xStart = side.sign * roadWidth;
            const xEnd = side.sign * (roadWidth + stripWidth);

            // Aktualizuj punkty: X (zależne od stripWidth), Z lokalne = -row*spacing, Y = baseLineY + height.
            for (let row = 0; row < rows; row++) {
                const localZ = -row * segmentSpacing;
                const worldZ = snappedZ + localZ;
                for (let col = 0; col < cols; col++) {
                    const i = (row * cols + col) * 3;
                    const t = col / (cols - 1);
                    const x = xStart + (xEnd - xStart) * t;
                    // edgeFactor: 0 przy krawędzi drogi, 1 przy zewnętrznej krawędzi (smoothstep).
                    const edge = t * t * (3 - 2 * t);
                    const y = baseLineY + this._heightAt(x, worldZ, edge);
                    fillPositions[i] = x;
                    fillPositions[i + 1] = y;
                    fillPositions[i + 2] = localZ;
                    points[row][col].x = x;
                    points[row][col].y = y;
                    points[row][col].z = localZ;
                }
            }
            side.fillMesh.geometry.attributes.position.needsUpdate = true;
            side.fillMesh.geometry.computeVertexNormals();
            side.fillMesh.geometry.computeBoundingSphere();

            this._rebuildWireframe(side);
        }
    }

    /**
     * Zbuduj/aktualizuj InstancedMesh wireframe dla boku (linie poziome + pionowe).
     */
    _rebuildWireframe(side) {
        const { rows, cols, lineWidth } = this.config;
        const lw = Math.max(lineWidth || 0.1, 0.001);

        // Policz segmenty: poziome rows*(cols-1) + pionowe (rows-1)*cols.
        const horizontal = rows * (cols - 1);
        const vertical = (rows - 1) * cols;
        const total = horizontal + vertical;

        let mesh = side.lineMesh;
        if (!mesh || side.lineCapacity < total) {
            if (mesh) {
                this.group.remove(mesh);
                mesh.dispose();
            }
            const capacity = Math.max(16, Math.ceil(total / 16) * 16);
            mesh = new THREE.InstancedMesh(this.unitCylinderGeometry, this.material, capacity);
            mesh.renderOrder = 1;
            mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
            mesh.frustumCulled = false;
            this.group.add(mesh);
            side.lineMesh = mesh;
            side.lineCapacity = capacity;
        }

        const dummy = new THREE.Object3D();
        const up = new THREE.Vector3(0, 1, 0);
        const dir = new THREE.Vector3();
        let idx = 0;

        const setInstance = (p1, p2) => {
            dir.set(p2.x - p1.x, p2.y - p1.y, p2.z - p1.z);
            const len = dir.length();
            if (len < 1e-6) {
                dummy.position.set(p1.x, p1.y, p1.z);
                dummy.scale.set(0, 0, 0);
                dummy.quaternion.set(0, 0, 0, 1);
            } else {
                dir.multiplyScalar(1 / len);
                dummy.position.set((p1.x + p2.x) / 2, (p1.y + p2.y) / 2, (p1.z + p2.z) / 2);
                dummy.scale.set(lw, len, lw);
                dummy.quaternion.setFromUnitVectors(up, dir);
            }
            dummy.updateMatrix();
            mesh.setMatrixAt(idx, dummy.matrix);
            idx++;
        };

        // Linie poziome (wzdłuż X)
        for (let row = 0; row < rows; row++) {
            for (let col = 0; col < cols - 1; col++) {
                setInstance(side.points[row][col], side.points[row][col + 1]);
            }
        }
        // Linie pionowe (wzdłuż Z)
        for (let col = 0; col < cols; col++) {
            for (let row = 0; row < rows - 1; row++) {
                setInstance(side.points[row][col], side.points[row + 1][col]);
            }
        }

        mesh.count = total;
        mesh.instanceMatrix.needsUpdate = true;
        mesh.computeBoundingSphere();
    }

    /**
     * Aktualizacja - przebuduj siatkę gdy snappedZ się zmieni (co segmentSpacing ruchu kamery).
     */
    update(cameraZ) {
        if (!this.config.enabled) return;
        const spacing = this.config.segmentSpacing;
        const snappedZ = Math.round(cameraZ / spacing) * spacing;
        if (snappedZ !== this.lastSnappedZ) {
            this.lastSnappedZ = snappedZ;
            this.rebuild(snappedZ);
        }
    }

    updateConfig(newConfig) {
        if (!newConfig) return;
        let needsRebuild = false;
        if (newConfig.height !== undefined) { this.config.height = newConfig.height; needsRebuild = true; }
        if (newConfig.stripWidth !== undefined) { this.config.stripWidth = newConfig.stripWidth; needsRebuild = true; }
        if (newConfig.noiseScale !== undefined) { this.config.noiseScale = newConfig.noiseScale; needsRebuild = true; }
        if (newConfig.glowIntensity !== undefined) {
            this.config.glowIntensity = newConfig.glowIntensity;
            const glowIntensity = newConfig.glowIntensity;
            const baseOpacity = 0.8;
            this.bloomBoost = Math.max(glowIntensity * 1.1, 2.5);
            this.material.color.setHex(this.baseColor).multiplyScalar(this.bloomBoost);
            this.material.opacity = Math.min(baseOpacity * glowIntensity, 1.0);
            this.material.needsUpdate = true;
        }
        if (newConfig.fillColor !== undefined) {
            this.config.fillColor = newConfig.fillColor;
            this.fillMaterial.color.setHex(newConfig.fillColor);
        }
        if (newConfig.solid !== undefined) {
            this.config.solid = newConfig.solid;
            for (const side of this.sides) {
                if (side.fillMesh) side.fillMesh.visible = newConfig.solid;
            }
        }
        if (newConfig.enabled !== undefined) {
            this.config.enabled = newConfig.enabled;
            this.group.visible = newConfig.enabled;
            if (newConfig.enabled) needsRebuild = true;
        }
        if (needsRebuild) {
            this.rebuild(this.lastSnappedZ || 0);
        }
    }

    dispose() {
        for (const side of this.sides) {
            if (side.fillMesh) {
                side.fillMesh.geometry.dispose();
                this.group.remove(side.fillMesh);
            }
            if (side.lineMesh) {
                side.lineMesh.dispose();
                this.group.remove(side.lineMesh);
            }
        }
        if (this.unitCylinderGeometry) this.unitCylinderGeometry.dispose();
        if (this.material) this.material.dispose();
        if (this.fillMaterial) this.fillMaterial.dispose();
        this.sides = [];
    }
}
